import React from 'react';
import classNames from 'classnames';

import { GLOBAL_SPACE } from '../../design-tokens/build/rc-tokens-web-typescript';

type GLOBAL_SIZES = typeof GLOBAL_SPACE[number];
const EXCLUDED_SIZES = [ 'rc-space-0', 'rc-space-px', 'rc-space-half', 'rc-space-three-quarters', 'rc-space-1', 'rc-space-1-and-half', 'rc-space-2'] as const;

type VALID_ICON_SIZES = Exclude<GLOBAL_SIZES, typeof EXCLUDED_SIZES[number]>;

export interface RCIconProps {
  /** A React icon component, `<svg>` element, or similar. */
  icon: JSX.Element;
  /** The rendered size of the icon.
   * - `fullWidth` – Default. Sets the icon width to 100% of its container.
   * - `inline` – Sizes the icon to be relative to the surrounding font size.
   * - `native` – Renders the icon at its native SVG size (28px).
   * - `rc-space-*` – Renders the icon at a fixed width using our global 4px size scale (e.g. `rc-space-5` = 20px)
  */
  size?: 'fullWidth' | 'inline' | 'native' | VALID_ICON_SIZES;
  /** @deprecated use `size = 'inline'` instead. Using this props overrides `size` prop. */
  inline?: boolean;
  /** @deprecated use `size = 'native'` instead. Using this props overrides `size` and `inline` props. */
  nativeSize?: boolean;
  /** Added as an `aria-label`. Leave this blank for decorative icons, but add a descriptive verb for interactive icons.
   *  Icons without a `title` wll be `aria-hidden={true}`
  */
  title?: string;
}

/**
 * A simple wrapper for SVG elements that handles icon sizing and accessibility.
 *
 * Icons are sized to 100% of their container's width by default.
*/
export function RCIcon({
  size: iconSize = 'fullWidth',
  inline = false,
  nativeSize = false,
  title = undefined,
  icon,
}: RCIconProps) {
  const size: RCIconProps['size'] = nativeSize ? 'native' : inline ? 'inline' : iconSize;

  const classes = classNames('rc-icon', {
    'rc-icon--inline': size === 'inline',
    'rc-icon--native-size': size === 'native',
  });

  const styleOverrides = {
    ...fixedSize(size),
  } as React.CSSProperties;

  return (
    <span
      className={classes}
      style={styleOverrides}
      aria-hidden={title ? undefined : true}
      aria-label={title || undefined}
      role={title ? 'img' : undefined}
    >
      {React.isValidElement(icon) &&
        React.cloneElement(icon, { 'aria-hidden': true } as React.HTMLAttributes<Element>)
      }
    </span>
  );
}

function fixedSize(size: RCIconProps['size']) {
  if (size === 'fullWidth') return {};
  if (size === 'inline') return {};
  if (size === 'native') return {};
  return { '--icon-size': `var(--${size})` };
}
